import { useContext, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Radio,
  Row,
  Table,
  Tabs,
  Tag,
  Upload,
  Image,
} from "antd";
import {
  BellOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  SendOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { AppStateContext } from "../../context/AppContext";
import { AuthContext } from "../../context/AuthContext";
import useRequest from "../../hooks/useRequest";
import { useDebouncedFunction } from "../../hooks/useDebounce";
import apiPath from "../../constants/apiPath";
import { Severty, ShowToast } from "../../helper/toast";

const { TextArea } = Input;

function NotificationManagementPage() {
  const { setPageHeading } = useContext(AppStateContext);
  setPageHeading("Notification Management");

  return (
    <div className="page-inner-headding">
      <NotificationManagement />
    </div>
  );
}

function NotificationManagement() {
  const { request } = useRequest();
  const { userProfile } = useContext(AuthContext);
  const userType = userProfile?.type || userProfile?.userType;
  const isSuperAdmin = userType === "SuperAdmin";
  const [form] = Form.useForm();
  const [targetType, setTargetType] = useState("all");
  const [sending, setSending] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [userList, setUserList] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [userPage, setUserPage] = useState(1);
  const [userLimit] = useState(15);
  const [search, setSearch] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [cronInterval, setCronInterval] = useState(30);
  const [cronLoading, setCronLoading] = useState(false);
  const [cronSaving, setCronSaving] = useState(false);

  const fetchUsers = (page = userPage, searchTerm = search) => {
    setUserLoading(true);
    const query = new URLSearchParams({
      page: String(page),
      limit: String(userLimit),
      hasFcm: "true",
    });
    if (searchTerm) query.append("search", searchTerm);

    request({
      url: `${apiPath.notificationUsers}?${query.toString()}`,
      method: "GET",
      onSuccess: ({ data }) => {
        setUserList(data?.list?.docs || []);
        setTotalDocs(data?.totalDocs || 0);
        setUserLoading(false);
      },
      onError: (err) => {
        ShowToast(err?.message || "Failed to load users", Severty.ERROR);
        setUserLoading(false);
      },
    });
  };

  const fetchHistory = (page = historyPage) => {
    setHistoryLoading(true);
    const query = new URLSearchParams({
      page: String(page),
      limit: "10",
    });
    request({
      url: `${apiPath.notificationHistory}?${query.toString()}`,
      method: "GET",
      onSuccess: ({ data }) => {
        setHistory(data?.list?.docs || []);
        setHistoryTotal(data?.totalDocs || 0);
        setHistoryLoading(false);
      },
      onError: (err) => {
        ShowToast(err?.message || "Failed to load history", Severty.ERROR);
        setHistoryLoading(false);
      },
    });
  };

  useEffect(() => {
    if (targetType === "selected") {
      fetchUsers(1, search);
    }
  }, [targetType]);

  useEffect(() => {
    fetchHistory(historyPage);
  }, [historyPage]);

  const fetchShiftCronSettings = () => {
    setCronLoading(true);
    request({
      url: apiPath.notificationShiftCronSettings,
      method: "GET",
      onSuccess: ({ data }) => {
        const mins = Number(data?.intervalMinutes);
        setCronInterval([15, 30, 60].includes(mins) ? mins : 30);
        setCronLoading(false);
      },
      onError: (err) => {
        ShowToast(err?.message || "Failed to load cron settings", Severty.ERROR);
        setCronLoading(false);
      },
    });
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchShiftCronSettings();
    }
  }, [isSuperAdmin]);

  const saveShiftCronSettings = () => {
    setCronSaving(true);
    request({
      url: apiPath.notificationShiftCronSettings,
      method: "PUT",
      data: { intervalMinutes: cronInterval },
      onSuccess: ({ message }) => {
        ShowToast(message || "Cron timing updated", Severty.SUCCESS);
        setCronSaving(false);
      },
      onError: (err) => {
        ShowToast(err?.message || "Failed to update cron timing", Severty.ERROR);
        setCronSaving(false);
      },
    });
  };

  const debouncedSearch = useDebouncedFunction((term) => {
    setSearch(term);
    setUserPage(1);
    fetchUsers(1, term);
  });

  const clearImage = () => {
    setImageUrl(null);
    setImageFile(null);
    form.setFieldsValue({ imageUrl: undefined, imageFile: undefined });
  };

  const handleImageUpload = async ({ file, onSuccess, onError }) => {
    const rawFile = file?.originFileObj || file;
    if (!rawFile) {
      onError?.(new Error("No file selected"));
      return;
    }

    const isImage =
      rawFile.type?.startsWith("image/") ||
      /\.(jpe?g|png|gif|webp|bmp|heic|heif)$/i.test(rawFile.name || "");
    if (!isImage) {
      ShowToast("Only image files are allowed", Severty.ERROR);
      onError?.(new Error("Invalid file type"));
      return;
    }

    const isLt5M = rawFile.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      ShowToast("Image must be smaller than 5MB", Severty.ERROR);
      onError?.(new Error("File too large"));
      return;
    }

    setImageUploading(true);
    const formData = new FormData();
    formData.append("image", rawFile);

    request({
      url: apiPath.notificationUploadImage,
      method: "POST",
      data: formData,
      onSuccess: (res) => {
        const url = res?.data?.imageUrl;
        const file = res?.data?.imageFile;
        if (!url || !file) {
          ShowToast("Upload failed — no URL returned", Severty.ERROR);
          setImageUploading(false);
          onError?.(new Error("No URL"));
          return;
        }
        setImageUrl(url);
        setImageFile(file);
        form.setFieldsValue({ imageUrl: url, imageFile: file });
        ShowToast("Image uploaded (deleted after send)", Severty.SUCCESS);
        setImageUploading(false);
        onSuccess?.("ok");
      },
      onError: (err) => {
        ShowToast(err?.message || "Image upload failed", Severty.ERROR);
        setImageUploading(false);
        onError?.(err);
      },
    });
  };

  const onSend = () => {
    form
      .validateFields()
      .then((values) => {
        const isAll = targetType === "all";
        const countLabel = isAll
          ? "all your users with FCM token"
          : `${selectedRowKeys.length} selected user(s)`;

        Modal.confirm({
          content: `This will send "${values.title}" to ${countLabel}.`,
          okText: "Send",
          cancelText: "Cancel",
          onOk: () => {
            if (!isAll && !selectedRowKeys.length) {
              ShowToast("Select at least one user", Severty.WARNING);
              return Promise.reject(new Error("Select at least one user"));
            }

            setSending(true);
            const payload = {
              title: values.title,
              body: values.body,
              targetType,
              imageFile: values.imageFile || imageFile || undefined,
            };
            if (!isAll) {
              payload.userIds = selectedRowKeys;
            }

            return new Promise((resolve, reject) => {
              request({
                url: apiPath.sendNotification,
                method: "POST",
                data: payload,
                onSuccess: ({ data, message }) => {
                  ShowToast(
                    message ||
                      `Sent: ${data?.successCount || 0} success, ${data?.failureCount || 0} failed`,
                    Severty.SUCCESS
                  );
                  form.resetFields();
                  setImageUrl(null);
                  setImageFile(null);
                  setSelectedRowKeys([]);
                  setHistoryPage(1);
                  fetchHistory(1);
                  setSending(false);
                  resolve();
                },
                onError: (err) => {
                  ShowToast(
                    err?.message || "Failed to send notification",
                    Severty.ERROR
                  );
                  setSending(false);
                  reject(
                    err instanceof Error
                      ? err
                      : new Error(err?.message || "Failed to send notification")
                  );
                },
              });
            });
          },
        });
      })
      .catch(() => {});
  };

  const userColumns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Mobile", dataIndex: "mobile", key: "mobile" },
    {
      title: "FCM",
      key: "hasFcm",
      render: (_, row) =>
        row.hasFcm ? (
          <Tag color="green">Ready</Tag>
        ) : (
          <Tag color="default">No token</Tag>
        ),
    },
  ];

  const historyColumns = [
    { title: "Title", dataIndex: "title", key: "title", ellipsis: true },
    {
      title: "Message",
      dataIndex: "body",
      key: "body",
      ellipsis: true,
      width: 160,
    },
    {
      title: "Target",
      dataIndex: "targetType",
      key: "targetType",
      render: (v) => (
        <Tag color={v === "all" ? "blue" : "purple"}>
          {v === "all" ? "All users" : "Selected"}
        </Tag>
      ),
    },
    {
      title: "Recipients",
      dataIndex: "totalRecipients",
      key: "totalRecipients",
      width: 100,
    },
    {
      title: "Success",
      dataIndex: "successCount",
      key: "successCount",
      width: 80,
    },
    {
      title: "Failed",
      dataIndex: "failureCount",
      key: "failureCount",
      width: 80,
    },
    {
      title: "Sent by",
      key: "sentBy",
      render: (_, row) => row.sentBy?.name || "—",
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (v) => (v ? new Date(v).toLocaleString() : "—"),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    preserveSelectedRowKeys: true,
  };

  const sendTab = (
    <>
      {isSuperAdmin && (
        <Card
          title={
            <span>
              <ClockCircleOutlined style={{ marginRight: 8 }} />
              Shift selfie reminder (automatic cron)
            </span>
          }
          bordered={false}
          style={{ marginBottom: 24 }}
          loading={cronLoading}
        >
          <p style={{ marginBottom: 16, color: "#666" }}>
            At each interval, active users receive a selfie reminder notification
            only while they are within their shift hours. Change the interval here
            (no server restart required).
          </p>
          <div className="d-flex flex-wrap align-items-center gap-3">
            <Radio.Group
              value={cronInterval}
              onChange={(e) => setCronInterval(e.target.value)}
              disabled={cronSaving}
            >
              <Radio.Button value={15}>15 minutes</Radio.Button>
              <Radio.Button value={30}>30 minutes</Radio.Button>
              <Radio.Button value={60}>60 minutes</Radio.Button>
            </Radio.Group>
            <Button
              type="primary"
              loading={cronSaving}
              onClick={saveShiftCronSettings}
            >
              Save timing
            </Button>
          </div>
        </Card>
      )}
      <Row gutter={[24, 24]}>
      <Col xs={24} lg={10}>
        <Card title="Compose notification" bordered={false}>
          <Form form={form} layout="vertical" onFinish={onSend}>
            <Form.Item
              name="title"
              label="Title"
              rules={[{ required: true, message: "Enter notification title" }]}
            >
              <Input placeholder="Notification title" maxLength={120} />
            </Form.Item>
            <Form.Item
              name="body"
              label="Message"
              rules={[{ required: true, message: "Enter notification message" }]}
            >
              <TextArea
                rows={4}
                placeholder="Notification message"
                maxLength={500}
                showCount
              />
            </Form.Item>
            <Form.Item name="imageUrl" hidden>
              <Input type="hidden" />
            </Form.Item>
            <Form.Item name="imageFile" hidden>
              <Input type="hidden" />
            </Form.Item>
            <Form.Item label="Notification image (optional)">
              <Upload
                accept="image/jpeg,image/png,image/gif,image/webp,image/*"
                showUploadList={false}
                customRequest={handleImageUpload}
                disabled={imageUploading}
                maxCount={1}
              >
                <Button type="button" icon={<UploadOutlined />} loading={imageUploading}>
                  Select image
                </Button>
              </Upload>
              {imageUrl && (
                <div style={{ marginTop: 12, position: "relative", display: "inline-block" }}>
                  <Image
                    src={imageUrl}
                    alt="Notification"
                    style={{ maxWidth: "100%", maxHeight: 160, borderRadius: 8 }}
                  />
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={clearImage}
                    style={{ position: "absolute", top: 4, right: 4, background: "#fff" }}
                  >
                    Remove
                  </Button>
                </div>
              )}
              <p style={{ fontSize: 12, color: "#888", marginTop: 6, marginBottom: 0 }}>
                Image is stored on server temporarily and removed automatically after a successful send.
              </p>
            </Form.Item>
            <Form.Item label="Send to">
              <Radio.Group
                value={targetType}
                onChange={(e) => {
                  setTargetType(e.target.value);
                  setSelectedRowKeys([]);
                }}
              >
                <Radio value="all">All your users (with FCM token)</Radio>
                <Radio value="selected">Selected users only</Radio>
              </Radio.Group>
            </Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SendOutlined />}
              loading={sending}
              block
            >
              Send notification
            </Button>
          </Form>
        </Card>
      </Col>
      {targetType === "selected" && (
        <Col xs={24} lg={14}>
          <Card
            title="Select users"
            bordered={false}
            extra={
              <span style={{ fontSize: 13, color: "#666" }}>
                {selectedRowKeys.length} selected
              </span>
            }
          >
            <Input.Search
              placeholder="Search by name or mobile"
              allowClear
              onChange={(e) => debouncedSearch(e.target.value)}
              style={{ marginBottom: 16 }}
            />
            <Table
              rowKey="_id"
              columns={userColumns}
              dataSource={userList}
              loading={userLoading}
              rowSelection={rowSelection}
              pagination={{
                current: userPage,
                pageSize: userLimit,
                total: totalDocs,
                showSizeChanger: false,
                onChange: (p) => {
                  setUserPage(p);
                  fetchUsers(p, search);
                },
              }}
              size="small"
            />
          </Card>
        </Col>
      )}
    </Row>
    </>
  );

  const historyTab = (
    <Table
      rowKey="_id"
      columns={historyColumns}
      dataSource={history}
      loading={historyLoading}
      pagination={{
        current: historyPage,
        pageSize: 10,
        total: historyTotal,
        onChange: setHistoryPage,
      }}
    />
  );

  return (
    <Tabs
      defaultActiveKey="send"
      items={[
        {
          key: "send",
          label: (
            <span>
              <SendOutlined /> Send
            </span>
          ),
          children: sendTab,
        },
        {
          key: "history",
          label: (
            <span>
              <BellOutlined /> History
            </span>
          ),
          children: historyTab,
        },
      ]}
    />
  );
}

export default NotificationManagementPage;
